import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  PLATFORM_LINK,
  PERSONAL_LINK,
  copyLink,
  isPartnerHelper
} from '../../../helpers/helperFunctions';
import {
  ACCOUNTING,
  HELPDESK,
  LOGIN,
  SETTINGS_ACCOUNT,
  SETTINGS_LAYOUT,
  SUPPORT_TICKETS
} from '../../../routes/routes';
import {
  Flexed,
  SectionSeparator,
  UserImageCircle
} from '../../../styles/GlobalComponents';
import { colors } from '../../../styles/GlobalStyles';
import {
  Icon as Icn,
  Icon,
  IconAccounting,
  IconCopy,
  IconHelpdesk,
  IconInviteLinks,
  IconLogout,
  IconSettings
} from '../../../styles/Icons';
import { FlexCenter } from '../../../styles/stylesStyled';
import { UserInfoType } from '../../../types/loginTypes';
import { useWindowWide } from '../../../helpers/wideScreen';
import {
  Collapsable,
  Menu,
  MenuInviteLabels,
  MenuInvitePlaceholder
} from '../BackofficeLayoutStyled';
import { useAppSelector } from '../../../app/hooks';
import { P2 } from '../../../styles/FontStyles';
import { ReferralLink } from '../../../views/Dashboard/DashboardStyled';
import {
  InviteLinksDashboard,
  ProfileInfo
} from '../../../views/Dashboard/InviteLinksDashboard';
import { ReplicatedLinks } from '../../../views/Dashboard/ReplicatedLinks';

const Overlay = styled.div<{ open: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(28, 23, 22, 0.1);
  backdrop-filter: blur(5px);
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.open ? '1' : '0')};
  z-index: 100999 !important;
  transition: all 0.5s ease;
  height: 100vh;
`;

const SidebarContainer = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: 100%;
  padding: 40px 20px;
  position: fixed;
  right: ${({ open }) => (open ? '0' : '-350px')};
  top: 0;
  bottom: 0;
  background: ${(props) => props.theme.appLayout.sideNavBackground};
  box-shadow: ${({ theme }) => theme.shadows.shadowXS};

  transition: all 0.3s ease;
  z-index: 4999999 !important;

  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  overflow: auto;
  height: 100vh;

  span {
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
  }
`;

interface ProfileDropdownType {
  menuIsOpen: boolean;
  userInfo: UserInfoType;
  setMenuIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const ProfileDropdown = ({
  menuIsOpen,
  userInfo,
  setMenuIsOpen
}: ProfileDropdownType) => {
  const { t } = useTranslation('layout');
  const history = useHistory();

  const wide = useWindowWide(800);

  const handleLogout = () => {
    history.push(LOGIN);
    setMenuIsOpen(false);
  };

  const referralUrl = `${PLATFORM_LINK}/ambassador/${userInfo?.user?.referralCode}/${userInfo?.user?.id}`;
  const personalUrl = `https://unitedone.global?uid=${userInfo?.user?.id}&h=${userInfo?.user?.referralCode}`;

  const isPartner = isPartnerHelper(
    userInfo?.user?.currentVirtualOfficeLicence?.dateEnd || ''
  );

  const MenuContent = () => {
    return (
      <div style={{ position: 'relative', marginBottom: 50 }}>
        <Flexed mt='60'></Flexed>

        <InviteLinksDashboard referralUrl={referralUrl} />

        <ReplicatedLinks referralUrl={personalUrl} />

        {isPartner && (
          <Link to={ACCOUNTING} onClick={() => setMenuIsOpen(!menuIsOpen)}>
            <FlexCenter style={{ marginBottom: 24 }}>
              <Icon pointer mr='16' color={colors.neutral900} w='25px'>
                <IconAccounting />
              </Icon>
              <P2>{t('profile.accounting')}</P2>
            </FlexCenter>
          </Link>
        )}

        <Link
          to={!wide ? SETTINGS_LAYOUT : SETTINGS_ACCOUNT}
          onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900} w='25px'>
              <IconSettings />
            </Icon>

            <P2>{t('profile.settings')}</P2>
          </FlexCenter>
        </Link>

        <Link to={SUPPORT_TICKETS} onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900} w='25px'>
              <IconHelpdesk />
            </Icon>
            <P2>Support Tickets</P2>
          </FlexCenter>
        </Link>

        <SectionSeparator />

        <Flexed
          center
          onClick={handleLogout}
          style={{ cursor: 'pointer' }}
          mt='20'>
          <Icon pointer mr='16' color={colors.neutral900} w='25px'>
            <IconLogout />
          </Icon>
          <P2>Logout</P2>
        </Flexed>
      </div>
    );
  };

  useEffect(() => {
    if (!wide && menuIsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'scroll';
    }
  }, [menuIsOpen, wide]);

  return (
    <>
      <Menu isOpen={menuIsOpen}>
        <DropdownHeader>
          <ProfileInfo user={userInfo?.user} />
        </DropdownHeader>
        <MenuContent />
      </Menu>
    </>
  );
};

const DropdownHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.primary900};
  margin-bottom: 12px;
  padding: 5px 10px;
`;
